"use client";

import type { ComponentType, SVGProps } from "react";

import {
  IconBadgeCheck20Line,
  IconEditPencil20Line,
  IconGroup20Line,
  IconPage20Line,
  IconSearch20Line,
  IconUser20Line,
} from "@/components/icons/iconoir";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import type { NavMegaMenuIcon, NavMegaMenuItem } from "./nav-mega-menu-data";

const ICON_MAP: Record<
  NavMegaMenuIcon,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  user: IconUser20Line,
  "badge-check": IconBadgeCheck20Line,
  group: IconGroup20Line,
  page: IconPage20Line,
  search: IconSearch20Line,
  "edit-pencil": IconEditPencil20Line,
};

export function NavigationDropdownItem({
  item,
  isActive,
  onHover,
  onFocus,
}: {
  item: NavMegaMenuItem;
  isActive: boolean;
  onHover: () => void;
  onFocus: () => void;
}) {
  const Icon = ICON_MAP[item.icon];

  return (
    <Link
      href={item.url}
      external={item.external}
      className={cn(
        "rounded-ds-l flex w-full items-center gap-3 border p-2 transition-[background-color,border-color] duration-200 ease-out",
        isActive ? "border-grey-100 bg-grey-50" : "bg-grey-0 border-transparent"
      )}
      onPointerEnter={onHover}
      onMouseEnter={onHover}
      onFocus={onFocus}
    >
      <div
        className={cn(
          "rounded-ds-m border-grey-100 flex size-12 shrink-0 items-center justify-center border transition-[background-color,color,box-shadow] duration-200 ease-out",
          isActive
            ? "bg-grey-950 text-grey-0 shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)]"
            : "bg-grey-50 text-grey-950 border-[0.714px]"
        )}
      >
        <Icon
          className={cn(
            "size-5 stroke-[1.5px] transition-colors duration-200 ease-out",
            isActive ? "text-grey-0" : "text-grey-950"
          )}
          aria-hidden
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-ds-body-md text-grey-950 leading-[1.5] tracking-[-0.02em]">
          {item.title}
        </p>
        <p className="text-ds-body-xs text-grey-500 truncate tracking-[-0.02em]">
          {item.description}
        </p>
      </div>
    </Link>
  );
}

export function MobileNavigationDropdownItem({
  item,
  onClick,
}: {
  item: NavMegaMenuItem;
  onClick?: () => void;
}) {
  const Icon = ICON_MAP[item.icon];

  return (
    <Link
      href={item.url}
      external={item.external}
      onClick={onClick}
      className="flex w-full items-center gap-4"
    >
      <div className="rounded-ds-m border-grey-100 bg-grey-50 flex size-9 shrink-0 items-center justify-center border">
        <Icon className="stroke-grey-950 size-5 stroke-[1.5px]" aria-hidden />
      </div>
      <span className="text-ds-body-md text-grey-950">{item.title}</span>
    </Link>
  );
}
